export type SupportedLocale = 'en' | 'es' | 'de' | 'uk';

export const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'es', 'de', 'uk'];
export const DEFAULT_LOCALE: SupportedLocale = 'en';

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  uk: 'Українська',
};

/**
 * Get the initial locale based on browser language or default
 */
export function getInitialLocale(): SupportedLocale {
  // Check browser language
  const browserLang = navigator.language.split('-')[0];
  
  if (SUPPORTED_LOCALES.includes(browserLang as SupportedLocale)) {
    return browserLang as SupportedLocale;
  }
  
  // Check localStorage
  const saved = localStorage.getItem('locale');
  if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
    return saved as SupportedLocale;
  }
  
  return DEFAULT_LOCALE;
}

/**
 * Extract locale from pathname
 */
export function getLocaleFromPath(pathname: string): SupportedLocale {
  const parts = pathname.split('/').filter(Boolean);
  const potentialLocale = parts[0];
  
  if (SUPPORTED_LOCALES.includes(potentialLocale as SupportedLocale)) {
    return potentialLocale as SupportedLocale;
  }
  
  return DEFAULT_LOCALE;
}

/**
 * Strip locale prefix from pathname
 */
export function stripLocaleFromPath(pathname: string): { locale: SupportedLocale; path: string } {
  const parts = pathname.split('/').filter(Boolean);
  const potentialLocale = parts[0];
  
  if (SUPPORTED_LOCALES.includes(potentialLocale as SupportedLocale)) {
    const path = '/' + parts.slice(1).join('/');
    return { locale: potentialLocale as SupportedLocale, path };
  }
  
  return { locale: DEFAULT_LOCALE, path: pathname };
}

/**
 * Build a localized path
 */
export function buildLocalePath(locale: SupportedLocale, path: string = '/'): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Remove trailing slash for non-root paths
  const cleanPath = normalizedPath === '/' ? '' : normalizedPath.replace(/\/$/, '');
  
  return `/${locale}${cleanPath}`;
}
